import { ApiError, apiRequest, getApiErrorMessage } from '../../lib/api';
import type { Assignment, BaAttendanceEvent, BaSalesSubmission } from '../../types/domain';

const databaseName = 'brand-field-crew-pwa';
const storeName = 'ba-outbox';
const databaseVersion = 1;

export type BaOutboxKind = 'attendance' | 'sales' | 'shelf_evidence' | 'survey' | 'activity_counter';

interface AttendancePayload {
  assignmentId: string;
  shiftId: string;
  eventType: 'check_in' | 'check_out';
  capturedAt: string;
  latitude: number;
  longitude: number;
  horizontalAccuracyM: number;
  locationCapturedAt: string;
  selfie: Blob;
}

interface SalesPayload {
  assignment_id: string;
  shift_id: string;
  sold_at: string;
  submission_type: 'sales' | 'no_sales';
  no_sales_reason: string;
  notes: string;
  idempotency_key: string;
  lines: Array<{ product_id: string; quantity: number }>;
  samples: Array<{ deal_id: string; product_id: string; quantity: number }>;
}

interface ShelfEvidencePayload {
  assignmentId: string;
  shiftId: string;
  capturedAt: string;
  latitude: number;
  longitude: number;
  horizontalAccuracyM: number;
  locationCapturedAt: string;
  photo: Blob;
  productIds: string[];
  inventoryCounts: Array<{ product_id: string; available_units: number }>;
  notes: string;
}

export type SurveyAnswerValue = boolean | string | string[];

interface SurveyPayload {
  surveyAssignmentId: string;
  outletMode: 'new' | 'existing_survey' | 'existing_catalogue' | 'activity_session';
  surveyOutletId: string | null;
  catalogueVenueId: string | null;
  activitySessionId: string | null;
  outletName: string;
  outletCity: string;
  outletArea: string;
  outletAddress: string;
  outletOwnerName: string;
  outletOwnerContact: string;
  answers: Array<{ question_id: string; value: SurveyAnswerValue }>;
  photo: Blob | null;
  capturedAt: string;
  latitude: number;
  longitude: number;
  horizontalAccuracyM: number;
  locationCapturedAt: string;
}

interface ActivityCounterPayload {
  sessionId: string;
  assignment_id: string;
  metric: 'student_interceptions' | 'samples' | 'doctor_consultations';
  quantity: number;
  captured_at: string;
  note: string;
}

interface ShelfEvidenceSubmission {
  id: string;
  status: string;
}

export interface BaOutboxItem {
  id: string;
  ownerUserId: string;
  kind: BaOutboxKind;
  label: string;
  createdAt: string;
  attempts: number;
  lastError: string | null;
  payload: AttendancePayload | SalesPayload | ShelfEvidencePayload | SurveyPayload | ActivityCounterPayload;
}

export interface BrowserLocationEvidence {
  latitude: number;
  longitude: number;
  horizontalAccuracyM: number;
  capturedAt: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion);
    request.onerror = () => reject(request.error ?? new Error('Offline storage could not be opened.'));
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(storeName)) {
        request.result.createObjectStore(storeName, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void,
): Promise<T> {
  const database = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => reject(transaction.error ?? new Error('Offline storage failed.'));
    work(transaction.objectStore(storeName), resolve, reject);
  });
}

export function createIdempotencyKey() {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export async function saveBaOutboxItem(item: BaOutboxItem): Promise<void> {
  await withStore<void>('readwrite', (store, resolve, reject) => {
    const request = store.put(item);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export async function listBaOutbox(ownerUserId: string): Promise<BaOutboxItem[]> {
  const rows = await withStore<BaOutboxItem[]>('readonly', (store, resolve, reject) => {
    const request = store.getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as BaOutboxItem[]);
  });
  return rows
    .filter((item) => item.ownerUserId === ownerUserId)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export async function removeBaOutboxItem(id: string): Promise<void> {
  await withStore<void>('readwrite', (store, resolve, reject) => {
    const request = store.delete(id);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export function attendanceOutboxItem(input: {
  ownerUserId: string;
  assignment: Assignment;
  eventType: 'check_in' | 'check_out';
  location: BrowserLocationEvidence;
  selfie: Blob;
}): BaOutboxItem {
  const id = createIdempotencyKey();
  return {
    id,
    ownerUserId: input.ownerUserId,
    kind: 'attendance',
    label: `${input.eventType === 'check_in' ? 'Check-in' : 'Checkout'} · ${input.assignment.shift.location.name}`,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    payload: {
      assignmentId: input.assignment.id,
      shiftId: input.assignment.shift.id,
      eventType: input.eventType,
      capturedAt: new Date().toISOString(),
      latitude: input.location.latitude,
      longitude: input.location.longitude,
      horizontalAccuracyM: input.location.horizontalAccuracyM,
      locationCapturedAt: input.location.capturedAt,
      selfie: input.selfie,
    } satisfies AttendancePayload,
  };
}

export function salesOutboxItem(input: {
  ownerUserId: string;
  assignment: Assignment;
  shiftId: string;
  soldAt: string;
  submissionType: 'sales' | 'no_sales';
  noSalesReason: string;
  notes: string;
  lines: Array<{ product_id: string; quantity: number }>;
  samples: Array<{ deal_id: string; product_id: string; quantity: number }>;
}): BaOutboxItem {
  const id = createIdempotencyKey();
  return {
    id,
    ownerUserId: input.ownerUserId,
    kind: 'sales',
    label: `${input.submissionType === 'no_sales' ? 'No sales' : 'Sales'} · ${input.assignment.shift.location.name}`,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    payload: {
      assignment_id: input.assignment.id,
      shift_id: input.shiftId,
      sold_at: input.soldAt,
      submission_type: input.submissionType,
      no_sales_reason: input.noSalesReason,
      notes: input.notes,
      idempotency_key: id,
      lines: input.lines,
      samples: input.samples,
    } satisfies SalesPayload,
  };
}

export function shelfEvidenceOutboxItem(input: {
  ownerUserId: string;
  assignment: Assignment;
  location: BrowserLocationEvidence;
  photo: Blob;
  productIds: string[];
  inventoryCounts: Array<{ product_id: string; available_units: number }>;
  notes: string;
}): BaOutboxItem {
  const id = createIdempotencyKey();
  return {
    id,
    ownerUserId: input.ownerUserId,
    kind: 'shelf_evidence',
    label: `Shelf evidence · ${input.assignment.shift.location.name}`,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    payload: {
      assignmentId: input.assignment.id,
      shiftId: input.assignment.shift.id,
      capturedAt: new Date().toISOString(),
      latitude: input.location.latitude,
      longitude: input.location.longitude,
      horizontalAccuracyM: input.location.horizontalAccuracyM,
      locationCapturedAt: input.location.capturedAt,
      photo: input.photo,
      productIds: input.productIds,
      inventoryCounts: input.inventoryCounts,
      notes: input.notes,
    } satisfies ShelfEvidencePayload,
  };
}

export function surveyOutboxItem(input: {
  ownerUserId: string;
  surveyAssignmentId: string;
  campaignName: string;
  outletMode: SurveyPayload['outletMode'];
  surveyOutletId?: string | null;
  catalogueVenueId?: string | null;
  activitySessionId?: string | null;
  outletName: string;
  outletCity: string;
  outletArea: string;
  outletAddress: string;
  outletOwnerName: string;
  outletOwnerContact: string;
  answers: SurveyPayload['answers'];
  photo: Blob | null;
  location: BrowserLocationEvidence;
}): BaOutboxItem {
  const id = createIdempotencyKey();
  return {
    id,
    ownerUserId: input.ownerUserId,
    kind: 'survey',
    label: `Survey · ${input.outletName || input.campaignName}`,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    payload: {
      surveyAssignmentId: input.surveyAssignmentId,
      outletMode: input.outletMode,
      surveyOutletId: input.surveyOutletId ?? null,
      catalogueVenueId: input.catalogueVenueId ?? null,
      activitySessionId: input.activitySessionId ?? null,
      outletName: input.outletName,
      outletCity: input.outletCity,
      outletArea: input.outletArea,
      outletAddress: input.outletAddress,
      outletOwnerName: input.outletOwnerName,
      outletOwnerContact: input.outletOwnerContact,
      answers: input.answers,
      photo: input.photo,
      capturedAt: new Date().toISOString(),
      latitude: input.location.latitude,
      longitude: input.location.longitude,
      horizontalAccuracyM: input.location.horizontalAccuracyM,
      locationCapturedAt: input.location.capturedAt,
    } satisfies SurveyPayload,
  };
}

export function activityCounterOutboxItem(input: {
  ownerUserId: string;
  sessionId: string;
  sessionLabel: string;
  assignmentId: string;
  metric: ActivityCounterPayload['metric'];
  quantity: number;
  note: string;
}): BaOutboxItem {
  const id = createIdempotencyKey();
  return {
    id,
    ownerUserId: input.ownerUserId,
    kind: 'activity_counter',
    label: `Activity count · ${input.sessionLabel}`,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    payload: {
      sessionId: input.sessionId,
      assignment_id: input.assignmentId,
      metric: input.metric,
      quantity: input.quantity,
      captured_at: new Date().toISOString(),
      note: input.note,
    } satisfies ActivityCounterPayload,
  };
}

export async function syncBaOutboxItem(
  item: BaOutboxItem,
  accessToken: string,
): Promise<BaAttendanceEvent | BaSalesSubmission | ShelfEvidenceSubmission | Record<string, unknown>> {
  const attempted = { ...item, attempts: item.attempts + 1, lastError: null };
  await saveBaOutboxItem(attempted);
  try {
    let response: BaAttendanceEvent | BaSalesSubmission | ShelfEvidenceSubmission | Record<string, unknown>;
    if (attempted.kind === 'attendance') {
      const payload = attempted.payload as AttendancePayload;
      const form = new FormData();
      form.set('assignment_id', payload.assignmentId);
      form.set('shift_id', payload.shiftId);
      form.set('event_type', payload.eventType);
      form.set('captured_at', payload.capturedAt);
      form.set('latitude', payload.latitude.toFixed(6));
      form.set('longitude', payload.longitude.toFixed(6));
      form.set('horizontal_accuracy_m', payload.horizontalAccuracyM.toFixed(2));
      form.set('location_captured_at', payload.locationCapturedAt);
      form.set('location_sample_count', '1');
      form.set('idempotency_key', attempted.id);
      form.set('app_version', 'web-pwa-1');
      form.set('device_time_offset_minutes', String(new Date().getTimezoneOffset()));
      form.set('selfie', payload.selfie, `${attempted.id}.jpg`);
      response = await apiRequest<BaAttendanceEvent>('/attendance/events/', {
        accessToken,
        method: 'POST',
        body: form,
      });
    } else if (attempted.kind === 'sales') {
      response = await apiRequest<BaSalesSubmission>('/sales/submissions/', {
        accessToken,
        method: 'POST',
        body: JSON.stringify(attempted.payload),
      });
    } else if (attempted.kind === 'shelf_evidence') {
      const payload = attempted.payload as ShelfEvidencePayload;
      const form = new FormData();
      form.set('assignment_id', payload.assignmentId);
      form.set('shift_id', payload.shiftId);
      payload.productIds.forEach((productId) => form.append('product_ids', productId));
      form.set('inventory_counts', JSON.stringify(payload.inventoryCounts));
      form.set('notes', payload.notes);
      form.set('captured_at', payload.capturedAt);
      form.set('latitude', payload.latitude.toFixed(6));
      form.set('longitude', payload.longitude.toFixed(6));
      form.set('horizontal_accuracy_m', payload.horizontalAccuracyM.toFixed(2));
      form.set('location_captured_at', payload.locationCapturedAt);
      form.set('location_sample_count', '1');
      form.set('idempotency_key', attempted.id);
      form.set('app_version', 'web-pwa-1');
      form.set('photo', payload.photo, `${attempted.id}.jpg`);
      response = await apiRequest<ShelfEvidenceSubmission>('/shelf-evidence/submissions/', {
        accessToken,
        method: 'POST',
        body: form,
      });
    } else if (attempted.kind === 'survey') {
      const payload = attempted.payload as SurveyPayload;
      const form = new FormData();
      form.set('survey_assignment_id', payload.surveyAssignmentId);
      form.set('outlet_mode', payload.outletMode);
      if (payload.surveyOutletId) form.set('survey_outlet_id', payload.surveyOutletId);
      if (payload.catalogueVenueId) form.set('catalogue_venue_id', payload.catalogueVenueId);
      if (payload.activitySessionId) form.set('activity_session_id', payload.activitySessionId);
      form.set('outlet_name', payload.outletName);
      form.set('outlet_city', payload.outletCity);
      form.set('outlet_area', payload.outletArea);
      form.set('outlet_address', payload.outletAddress);
      form.set('outlet_owner_name', payload.outletOwnerName);
      form.set('outlet_owner_contact', payload.outletOwnerContact);
      form.set('answers', JSON.stringify(payload.answers));
      if (payload.photo) form.set('storefront_photo', payload.photo, `${attempted.id}.jpg`);
      form.set('captured_at', payload.capturedAt);
      form.set('latitude', payload.latitude.toFixed(7));
      form.set('longitude', payload.longitude.toFixed(7));
      form.set('horizontal_accuracy_m', payload.horizontalAccuracyM.toFixed(2));
      form.set('location_captured_at', payload.locationCapturedAt);
      form.set('location_sample_count', '1');
      form.set('location_max_drift_m', '0.00');
      form.set('idempotency_key', attempted.id);
      form.set('app_version', 'web-pwa-1');
      response = await apiRequest<Record<string, unknown>>('/surveys/visits/', {
        accessToken,
        method: 'POST',
        body: form,
      });
    } else {
      const payload = attempted.payload as ActivityCounterPayload;
      response = await apiRequest<Record<string, unknown>>(`/activity-sessions/${payload.sessionId}/counters/`, {
        accessToken,
        method: 'POST',
        body: JSON.stringify({
          assignment_id: payload.assignment_id,
          metric: payload.metric,
          quantity: payload.quantity,
          captured_at: payload.captured_at,
          note: payload.note,
          idempotency_key: attempted.id,
        }),
      });
    }
    await removeBaOutboxItem(attempted.id);
    return response;
  } catch (error) {
    if (isTemporaryPwaFailure(error)) {
      await saveBaOutboxItem({ ...attempted, lastError: getApiErrorMessage(error) });
    } else {
      await removeBaOutboxItem(attempted.id);
    }
    throw error;
  }
}

/** Network and server-transient failures are safe to retain in the on-device retry queue. */
export function isTemporaryPwaFailure(error: unknown) {
  if (!(error instanceof ApiError)) return true;
  return error.status === 0 || error.status === 408 || error.status === 425 || error.status === 429 || error.status >= 500;
}

export async function captureBrowserLocation(): Promise<BrowserLocationEvidence> {
  if (!navigator.geolocation) throw new Error('Location is not supported by this browser.');
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        horizontalAccuracyM: position.coords.accuracy,
        capturedAt: new Date(position.timestamp).toISOString(),
      }),
      (error) => reject(new Error(
        error.code === error.PERMISSION_DENIED
          ? 'Location access is blocked. Allow location for this site in your browser settings and for your browser in your device settings, then try again.'
          : 'Your current location could not be captured. Move near a window and try again.',
      )),
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    );
  });
}

export function saveBaSnapshot<T>(ownerUserId: string, key: string, value: T) {
  localStorage.setItem(`brand-field-crew.ba.${ownerUserId}.${key}`, JSON.stringify({
    savedAt: new Date().toISOString(),
    value,
  }));
}

export function readBaSnapshot<T>(ownerUserId: string, key: string): { savedAt: string; value: T } | null {
  const raw = localStorage.getItem(`brand-field-crew.ba.${ownerUserId}.${key}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { savedAt: string; value: T };
  } catch {
    return null;
  }
}

export function removeBaSnapshot(ownerUserId: string, key: string) {
  localStorage.removeItem(`brand-field-crew.ba.${ownerUserId}.${key}`);
}
